import React, { useCallback, useEffect, useState } from "react";
import { api, errMsg, formatCurrency, formatDate } from "../lib/api";
import { Download, History, Calculator, CheckCircle2, XCircle, Clock, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Archive of generated cost-estimate PDFs + approval sign-off log. Any teammate
 * or auditor can download the exact PDF that was issued, and see who approved
 * it, when, and on whose authority.
 */
export default function CostEstimateHistory({ projectId, refreshKey, savePathHint }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/cost-estimates`);
      setItems(data || []);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  }, [projectId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const download = async (it) => {
    try {
      const { data } = await api.get(`/projects/${projectId}/cost-estimates/${it.id}`);
      const bytes = Uint8Array.from(atob(data.pdf_base64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = it.filename || `cost-estimate_${it.id}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setApproval = async (it, body) => {
    try {
      await api.post(`/projects/${projectId}/cost-estimates/${it.id}/approval`, body);
      toast.success(
        body.status === "approved" ? `Signed off by ${body.stakeholder_name}` :
        body.status === "rejected" ? `Marked rejected by ${body.stakeholder_name}` :
        "Approval cleared — back to pending"
      );
      setEditingId("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-5 border-t border-slate-800 pt-4" data-testid="cost-estimate-history">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 mb-1">
        <Calculator size={12}/> Cost estimate archive
      </div>
      <div className="text-[11px] text-slate-500 mb-3">
        Every cost estimate ever generated is stored here with a sign-off log so teammates and auditors can see who approved it and when.
        {savePathHint && <> Each PDF downloads locally to <span className="text-slate-300 mono">{savePathHint}</span> (your browser's Downloads folder).</>}
      </div>
      {loading ? (
        <div className="text-slate-500 text-sm">Loading archive…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 text-sm" data-testid="cost-estimate-empty">
          No cost estimates yet. Click <strong className="text-slate-300">Cost estimate PDF</strong> to create the first one.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <CostEstimateRow
              key={it.id}
              it={it}
              editing={editingId === it.id}
              onEdit={(v) => setEditingId(v ? it.id : "")}
              onDownload={() => download(it)}
              onSetApproval={(body) => setApproval(it, body)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CostEstimateRow({ it, editing, onEdit, onDownload, onSetApproval }) {
  const status = it.approval_status || "pending";
  const [name, setName] = useState(it.approved_by || "");
  const [when, setWhen] = useState((it.approved_at || "").slice(0, 10) || new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState(it.approval_note || "");

  return (
    <div className="p-3 rounded-lg bg-slate-900/40 border border-slate-800" data-testid={`cost-estimate-row-${it.id}`}>
      <div className="flex items-center gap-3">
        <History size={14} className="text-slate-500 shrink-0"/>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white font-medium truncate">{it.filename}</div>
          <div className="text-[11px] text-slate-500">
            {formatDate(it.created_at)} · by <span className="text-slate-300">{it.author_name || "—"}</span>
            {it.metrics?.total_estimated_cost != null && <> · total <span className="text-emerald-300">{formatCurrency(it.metrics.total_estimated_cost)}</span></>}
            {it.metrics?.task_count != null && <> · {it.metrics.task_count} tasks</>}
          </div>
        </div>
        <StatusBadge status={status} it={it}/>
        {!editing ? (
          <button
            onClick={() => onEdit(true)}
            data-testid={`cost-estimate-approve-btn-${it.id}`}
            className="text-xs text-slate-200 hover:text-white inline-flex items-center gap-1 border border-slate-700 hover:border-blue-500 rounded px-2 py-1"
          >
            {status === "pending" ? <>Mark approved</> : <>Edit sign-off</>}
          </button>
        ) : null}
        <button
          onClick={onDownload}
          data-testid={`cost-estimate-download-${it.id}`}
          className="text-xs text-blue-300 hover:text-blue-200 inline-flex items-center gap-1 border border-slate-700 rounded px-2 py-1"
        >
          <Download size={12}/> PDF
        </button>
      </div>
      {editing && (
        <div className="mt-3 pt-3 border-t border-slate-800 grid gap-2 sm:grid-cols-[1fr_140px_1fr_auto] items-end" data-testid={`cost-estimate-approval-form-${it.id}`}>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Stakeholder name</label>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Jane Doe, VP Operations"
              data-testid={`approval-name-${it.id}`}
              className="w-full px-2.5 py-1.5 rounded-lg border focus:outline-none focus:border-blue-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Date</label>
            <input
              type="date" value={when} onChange={(e) => setWhen(e.target.value)}
              data-testid={`approval-date-${it.id}`}
              className="w-full px-2.5 py-1.5 rounded-lg border focus:outline-none focus:border-blue-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Note (optional)</label>
            <input
              type="text" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Signed via email, see thread…"
              data-testid={`approval-note-${it.id}`}
              className="w-full px-2.5 py-1.5 rounded-lg border focus:outline-none focus:border-blue-500 text-sm"
            />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <button
              onClick={() => onSetApproval({ status: "approved", stakeholder_name: name.trim(), approved_at: new Date(when).toISOString(), note })}
              disabled={!name.trim()}
              data-testid={`approve-btn-${it.id}`}
              className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs px-2.5 py-1.5 rounded-lg disabled:opacity-50"
              title="Record approval"
            >
              <CheckCircle2 size={12}/> Approve
            </button>
            <button
              onClick={() => onSetApproval({ status: "rejected", stakeholder_name: name.trim(), approved_at: new Date(when).toISOString(), note })}
              disabled={!name.trim()}
              data-testid={`reject-btn-${it.id}`}
              className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-500 text-white text-xs px-2.5 py-1.5 rounded-lg disabled:opacity-50"
              title="Record rejection"
            >
              <XCircle size={12}/> Reject
            </button>
            {status !== "pending" && (
              <button
                onClick={() => onSetApproval({ status: "pending" })}
                data-testid={`clear-approval-btn-${it.id}`}
                className="inline-flex items-center gap-1 text-slate-300 hover:text-white text-xs px-2.5 py-1.5 rounded-lg border border-slate-700 hover:border-slate-500"
                title="Reset to pending"
              >
                <RotateCcw size={12}/> Clear
              </button>
            )}
            <button
              onClick={() => onEdit(false)}
              className="text-xs text-slate-400 hover:text-white px-2 py-1.5"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {!editing && status !== "pending" && (
        <div className="mt-2 ml-7 text-[11px] text-slate-400 leading-snug">
          {status === "approved" ? "Approved" : "Rejected"} by <span className="text-slate-200">{it.approved_by}</span>
          {it.approved_at && <> on <span className="text-slate-200">{formatDate(it.approved_at)}</span></>}
          {it.approval_recorded_by && <span className="text-slate-500"> · recorded by {it.approval_recorded_by}</span>}
          {it.approval_note && <div className="text-slate-300 italic mt-0.5">"{it.approval_note}"</div>}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "approved") return (
    <span data-testid="approval-badge-approved" className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
      <CheckCircle2 size={12}/> Approved
    </span>
  );
  if (status === "rejected") return (
    <span data-testid="approval-badge-rejected" className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border border-rose-500/40 bg-rose-500/10 text-rose-300">
      <XCircle size={12}/> Rejected
    </span>
  );
  return (
    <span data-testid="approval-badge-pending" className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border border-slate-700 text-slate-400">
      <Clock size={12}/> Pending
    </span>
  );
}
